import type { PImage } from '../../../engine/aliases'
import type { IDeadish, IEntity } from '../../../entities/entitiesUtils'
import { entitiesUtils } from '../../../entities/entitiesUtils-sidecar'
import { l } from '../../../lib/imports'
import type { ISpring } from '../../../lib/spring'
import { spring } from '../../../lib/spring-sidecar'
import { common } from '../../../main/common-sidecar'
import { sprites } from '../../../sprites/sprites-sidecar'
import { spritesUtil } from '../../../sprites/spritesUtil-sidecar'
import type { IStevieText2 } from '../../../uiParts/stevieTexts2'
import { stevieTexts2 } from '../../../uiParts/stevieTexts2-sidecar'
import { chessLayers } from '../chessLayers-sidecar'
import { EChessTeam } from '../chessPieces'
import { chessPieces } from '../chessPieces-sidecar'
import { EChessClientRequest } from '../messages/chessMessageClientRequest'
import type { IChessMessageClientRequestResponse } from '../messages/chessMessageClientRequestResponse'
import { EChessCursorPickCommand } from '../messages/chessMessageCursorPick'
import { chessSounds } from '../sound/chessSounds-sidecar'
import { chessFonts } from './chessFonts-sidecar'

export const mouseCursorUpdateTime = 1 / 10

export interface IChessMultiplayerCursor extends IEntity, IDeadish {
  team: EChessTeam
  multiId: number
  name: string
  imageCursor: PImage
  imageShadow: PImage
  textCursor: IStevieText2
  selectedPieceId: number

  springX: ISpring
  springY: ISpring

  updateTimer: number
  hideTimer: number
  isHidden: boolean
}

const objs: IChessMultiplayerCursor[] = []
export function getAll() {
  return objs
}

export function findByMultiId(multiId: number) {
  return l.find(getAll(), (c) => c.multiId == multiId) || null
}
export function findByMultiIdSafe(multiId: number) {
  let cur = findByMultiId(multiId)
  if (!cur) {
    cur = create(multiId)
  }
  return cur
}

export function create(multiId: number): IChessMultiplayerCursor {
  let frame = sprites.chessCursor[0]
  let imageCursor = spritesUtil.createImage(
    chessLayers.getLayers().abovePieces,
    frame,
  )
  imageCursor.scale = 0.25
  imageCursor.alpha = 0.75
  // imageCursor.tint = chessColors.red

  let textCursor = stevieTexts2.create(
    chessFonts.get().fBubble,
    0,
    0,
    chessLayers.getLayers().abovePieces,
  )
  stevieTexts2.setScale(textCursor, 0.4)
  stevieTexts2.setText(textCursor, '' + multiId)

  let imageShadow = spritesUtil.createImage(
    chessLayers.getLayers().pieces,
    sprites.chessShadowBlack[0],
  )
  imageShadow.scale = 1
  imageShadow.alpha = 0

  let obj: IChessMultiplayerCursor = {
    id: -1,
    idLoaded: -1,
    selectedPieceId: -1,
    multiId,
    name: 'anon',
    isDead: false,
    eType: 'voy-misc',
    imageCursor: imageCursor,
    imageShadow: imageShadow,
    textCursor: textCursor,
    springX: spring.create(),
    springY: spring.create(),
    updateTimer: 0,
    hideTimer: 999,
    isHidden: true,
    team: EChessTeam.Spectator,
  }
  objs.push(obj)

  spring.setDef(obj.springX, spring.defTight())
  spring.setDef(obj.springY, spring.defTight())

  return obj
}

export function moveToXY(obj: IChessMultiplayerCursor, x: number, y: number) {
  obj.imageCursor.x = x
  obj.imageCursor.y = y
  stevieTexts2.moveTo(obj.textCursor, x + 32, y)
}

export function kill(obj: IChessMultiplayerCursor) {
  obj.isDead = true
}
export function killAll() {
  l.forEach(objs, (c) => {
    kill(c)
  })
  removeDead()
}

export function updateAll() {
  l.forEach(objs, (c) => {
    update(c)
  })
  removeDead()
}
export function removeDead() {
  entitiesUtils.removeDead(objs, (c) => {
    c.imageCursor.destroy()
    c.imageShadow.destroy()
    stevieTexts2.kill(c.textCursor)
  })
}
function update(obj: IChessMultiplayerCursor) {
  let dt = common.getUpdateDelta()
  obj.updateTimer += dt
  obj.hideTimer += dt

  if (obj.hideTimer > 5) {
    obj.isHidden = true
  } else {
    obj.isHidden = false
  }
  obj.imageCursor.visible = !obj.isHidden
  stevieTexts2.setVisible(obj.textCursor, !obj.isHidden)

  spring.update(obj.springX, dt)
  spring.update(obj.springY, dt)

  moveToXY(obj, obj.springX.position, obj.springY.position)

  let sp = chessPieces.findByIdSafe(obj.selectedPieceId)
  if (sp) {
    chessPieces.moveToXY(sp, obj.springX.position, obj.springY.position)
    chessPieces.moveExternalShadow2To(obj.imageShadow, sp)
  } else {
    obj.imageShadow.visible = false
  }
}

export function networkRecieveStartLerpToXY(
  obj: IChessMultiplayerCursor,
  x: number,
  y: number,
) {
  obj.springX.target = x
  obj.springY.target = y
  obj.updateTimer = 0
  obj.hideTimer = 0
}

export function networkRecievePick(
  obj: IChessMultiplayerCursor,
  pieceId: number,
  command: EChessCursorPickCommand,
) {
  if (command === EChessCursorPickCommand.Putdown) {
    let sp = chessPieces.findByIdSafe(obj.selectedPieceId)
    if (sp && !sp.isDead) {
      chessPieces.moveToIJ(sp, sp, false)
      chessSounds.playPiecePutdown()
      chessPieces.createDust(sp)
    }
    obj.selectedPieceId = 0
  }
  if (command === EChessCursorPickCommand.Pickup) {
    obj.selectedPieceId = pieceId
    let sp = chessPieces.findByIdSafe(obj.selectedPieceId)
    if (sp && !sp.isDead) {
      chessSounds.playPiecePickup()
      chessPieces.createDust(sp)
    } else {
      obj.selectedPieceId = 0
    }
  }
}

export function networkRecieveTeam(msg: IChessMessageClientRequestResponse) {
  let obj = findByMultiIdSafe(msg.multiId)

  if (obj) {
    const sitAt = (team: EChessTeam) => {
      l.forEach(objs, (c) => {
        if (c.team === team) {
          c.team = EChessTeam.Spectator
        }
      })
      obj.team = team
    }

    if (msg.command === EChessClientRequest.SitWhite) {
      sitAt(EChessTeam.White)
    }
    if (msg.command === EChessClientRequest.SitBlack) {
      sitAt(EChessTeam.Black)
    }
    if (msg.command === EChessClientRequest.Spectate) {
      sitAt(EChessTeam.Spectator)
    }
  }
}

export function setName(obj: IChessMultiplayerCursor, name: string) {
  obj.name = name
  stevieTexts2.setText(obj.textCursor, name)
}
